import { SlideCaptureCanvas } from "@/components/slides/screenshot/SlideCaptureCanvas";
import { useFragment } from "@/generated";
import {
  GetSlidesByIdsDocument,
  SlideFieldsFragmentDoc,
  UpdateSlideDocument,
} from "@/generated/graphql";
import { isFreeformCanvas } from "@/lib/constants/slides";
import { useSlideScreenshot } from "@/lib/hooks/slides/useSlideScreenshot";
import type { SlideFields } from "@/types/Slide";
import { useApolloClient, useMutation } from "@apollo/client/react";
import {
  type ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

const IMAGE_LOAD_TIMEOUT_MS = 5000;
const CAPTURE_TIMEOUT_MS = 20000;
/** One retry per slide: the re-read alone settles a capture whose upload landed but whose reply didn't. */
const MAX_ATTEMPTS = 2;

const EnqueueContext = createContext<(slideIds: string[]) => void>(() => {});

/** Hands slides to the background screenshot queue; the caller never waits on the result. */
export function useSlideScreenshotQueue() {
  return useContext(EnqueueContext);
}

/** html-to-image drops images that have not loaded, so a shot waits for them — but never forever. */
async function waitForImages(node: HTMLElement) {
  const pending = Array.from(node.querySelectorAll("img"))
    .filter((image) => !image.complete)
    .map(
      (image) =>
        new Promise<void>((resolve) => {
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        })
    );

  if (pending.length === 0) return;

  await Promise.race([
    Promise.all(pending),
    new Promise((resolve) => setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS)),
  ]);
}

/** html-to-image re-fetches every image to inline it, and one fetch that never settles would stall the queue. */
function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    work,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Capture timed out")), ms)),
  ]);
}

/**
 * Screenshots slides the author never opened in the editor — the ones a generation run writes.
 * A freeform slide's thumbnail is an uploaded capture of its canvas, which only a browser can take,
 * so each slide is rendered offscreen one at a time and shot there. A slide stays queued until its
 * thumbnail lands or its attempts run out, and anything given up on is reported rather than
 * swallowed: the author gets that thumbnail the next time they save the slide by hand.
 */
export function SlideScreenshotQueueProvider({ children }: { children: ReactNode }) {
  const [rendering, setRendering] = useState<SlideFields | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;

  const client = useApolloClient();
  // The mutation is used bare rather than through useUpdateSlide, whose selection context only
  // exists on the pages that list slides. Apollo normalizes the slide it returns, so the new
  // thumbnail reaches every list already showing it.
  const [uploadScreenshot] = useMutation(UpdateSlideDocument);
  const { captureScreenshotAsFile } = useSlideScreenshot("");

  const pending = useRef<string[]>([]);
  const attempts = useRef(new Map<string, number>());
  const queuedThisSession = useRef(new Set<string>());
  const isDraining = useRef(false);
  const onRendered = useRef<(() => void) | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the commit of `rendering` is the signal
  useEffect(() => {
    onRendered.current?.();
    onRendered.current = null;
  }, [rendering]);

  /** Resolves once the offscreen canvas holds this slide, so the shot sees what it expects. */
  const render = (slide: SlideFields | null) =>
    new Promise<void>((resolve) => {
      onRendered.current = resolve;
      setRendering(slide);
    });

  /** Shoots one slide, reporting whether the thumbnail actually landed. */
  const capture = async (slide: SlideFields) => {
    try {
      await render(slide);
      if (!canvasRef.current) return false;

      await waitForImages(canvasRef.current);

      const file = await withTimeout(
        captureScreenshotAsFile(canvasRef, slide.id),
        CAPTURE_TIMEOUT_MS
      );
      if (!file) return false;

      await uploadScreenshot({
        variables: {
          input: { slideId: slide.id, slideData: slide.slideData },
          screenshotFile: file,
        },
      });
      return true;
    } catch (error) {
      console.debug("Slide screenshot failed", slide.id, error);
      return false;
    }
  };

  /** Stops tracking a slide: it has its thumbnail, or it is owed none. */
  const settle = (slideId: string) => {
    pending.current = pending.current.filter((one) => one !== slideId);
    attempts.current.delete(slideId);
  };

  /** Charges one attempt against a slide, giving up on it once they are spent. */
  const giveUpAfterAttempt = (slideId: string) => {
    const used = (attempts.current.get(slideId) ?? 0) + 1;
    attempts.current.set(slideId, used);
    if (used < MAX_ATTEMPTS) return false;
    settle(slideId);
    return true;
  };

  const readSlides = async (ids: string[]) => {
    const { data } = await client.query({
      query: GetSlidesByIdsDocument,
      variables: { ids },
      fetchPolicy: "network-only",
    });
    return (data?.getSlidesByIds ?? []).map((one) => useFragment(SlideFieldsFragmentDoc, one));
  };

  const drain = async () => {
    if (isDraining.current) return;
    isDraining.current = true;

    let abandoned = 0;

    try {
      while (pending.current.length > 0) {
        const ids = [...new Set(pending.current)];

        const slides = await readSlides(ids).catch((error) => {
          console.debug("Slide screenshot queue could not read its slides", error);
          return null;
        });

        // Ids stay queued across a failed read, so it costs the batch an attempt, not the batch.
        if (!slides) {
          for (const id of ids) {
            if (giveUpAfterAttempt(id)) abandoned += 1;
          }
          continue;
        }

        const returned = new Set(slides.map((one) => one.id));
        for (const id of ids) {
          if (!returned.has(id)) settle(id);
        }

        for (const slide of slides) {
          if (slide.imageId || !isFreeformCanvas(slide.type, slide.knowledgeArea)) {
            settle(slide.id);
            continue;
          }
          if (await capture(slide)) settle(slide.id);
          else if (giveUpAfterAttempt(slide.id)) abandoned += 1;
        }
      }
    } catch (error) {
      // Whatever is still queued keeps its place for the next enqueue rather than being dropped.
      console.debug("Slide screenshot queue stopped", error);
    } finally {
      isDraining.current = false;
      await render(null);
      if (abandoned > 0) {
        toast.error(
          `${abandoned} slide thumbnail${abandoned === 1 ? "" : "s"} could not be captured. Open the slide and save it to get one.`
        );
      }
    }
  };

  // Draining is always done by the newest render's closure, which keeps `enqueue` stable for
  // consumers that would otherwise re-run on every render of this provider.
  const drainRef = useRef(drain);
  drainRef.current = drain;

  // A slide is offered once per session: the lesson list re-enqueues whatever is still unshot
  // every time it is opened, and a canvas that cannot be captured must not cost an upload each time.
  const enqueue = useCallback((slideIds: string[]) => {
    const fresh = slideIds.filter((id) => !queuedThisSession.current.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) queuedThisSession.current.add(id);
    pending.current.push(...fresh);
    void drainRef.current();
  }, []);

  return (
    <EnqueueContext.Provider value={enqueue}>
      {children}
      {rendering && (
        <div
          aria-hidden
          // Offscreen rather than hidden: html-to-image needs the canvas laid out for real.
          style={{ position: "fixed", top: 0, left: "-10000px", pointerEvents: "none" }}
        >
          <SlideCaptureCanvas slide={rendering} canvasRef={canvasRef} />
        </div>
      )}
    </EnqueueContext.Provider>
  );
}
